import { withAuth, hasAuthParams } from 'react-oidc-context';
import React, { Component } from 'react';
import IdleLogout from '../components/IdleLogout';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { createBrowserHistory } from 'history';
import { getAuthUserData } from '../actions/menuActions';
import { requestMenuData } from '../slices/rootmenuSlice';
import {
  systemHideContextMenu,
  toggleMenu,
  updateAllowedMimeTypes,
} from '../slices/systemSlice';
import ErrorBoundary from '../components/ErrorBoundary';
import WithModals from '../components/HOC/withModals';
import MainHeader from '../components/mainheaders/MainHeader';
import Text from '../components/Text';
import localeLookup from '../config/locale';
import routes from '../config/routes';
import { getLogoutThresholds } from '../services/champService';
import { getTranslationsService } from '../services/menuService';
import { getPersons } from '../slices/personsSlice';
import { getWildcardPersons } from '../slices/wildcardPersonsSlice';
import { getQueryStringParams } from '../utils/helpers';
import {
  initiateSupport,
  resetSupportSession,
  setSupportUserProperties,
} from '../utils/support';

import { systemSelector } from './../reducers/systemReducer';
import RootNavigator from './RootNavigator';
import LoadOverlay from '../components/LoadOverlay';
import { getCSRFTokenService } from '../services/authService';
import { getGroups } from '../slices/groupsSlice';
import NetworkInfo from '../components/NetworkInfo';
import { ToastContainer } from 'react-toastify';
import { setMonitoringContext } from '../utils/monitoring';

const mapStateToProps = function (state) {
  return {
    blockUI: systemSelector.blockUI(state),
    menuOpen: systemSelector.menuOpen(state),
    showLoadOverlay: state.system.showLoadOverlay,
    notifications: state.notifications,
    locale: state.user.locale,
    userFullName: state.user.name,
    user: state.user,
  };
};
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      requestMenuData,
      getPersons,
      getWildcardPersons,
      updateAllowedMimeTypes,
      getAuthUserData,
      toggleMenu,
      systemHideContextMenu,
      getGroups,
    },
    dispatch
  );

export class App extends Component {
  constructor(props) {
    super(props);
    let prevLocation = props.history.location;
    this.props.history.listen((location) => {
      if (location.pathname !== prevLocation.pathname) {
        props.systemHideContextMenu();
        props.requestMenuData();
        if (!location.pathname.startsWith('/persons/all')) {
          props.getPersons();
          props.getGroups();
        }
      }
      prevLocation = location;
    });
    this.state = {
      hasTranslations: false,
      isGettingInitialState: true,
      hasFetchedInitialData: false,
      timeouts: 0,
      idleTimeout: 0,
      logoutTimeout: 0,
      modalThreshold: 0,
      logoutOnIdle: false,
      logoutThreshold: 0,
    };
    this.idleTimer = React.createRef();
  }

  async componentDidMount() {
    const queryParams = getQueryStringParams(window.location.search);
    const lsRememberMe = localStorage.getItem('rememberMe');
    if (queryParams.sign_in) {
      this.signInWithCredentials = true;
    }
    if (queryParams.remember_me) {
      this.setState({ logoutOnIdle: queryParams.remember_me === 'False' });
      localStorage.setItem('rememberMe', queryParams.remember_me);
    } else if (lsRememberMe) {
      this.setState({ logoutOnIdle: lsRememberMe === 'False' });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      locale,
      userData,
      requestMenuData,
      getAuthUserData,
      signinRedirect,
      activeNavigator,
      auth,
    } = this.props;
    // Automatic login - guard against redirecting while processing auth callback
    const { isAuthenticated, isLoading } = auth;
    if (
      !auth.activeNavigator &&
      ((!isAuthenticated && !isLoading && !hasAuthParams(window.location)) ||
        auth.error)
    ) {
      console.log('User is not authenticated, redirecting to login...');
      auth.signinRedirect();
    }

    if (!prevProps.auth.isAuthenticated && isAuthenticated) {
      getCSRFTokenService();
      getAuthUserData().then(({ tenantId, tenants, userId }) => {
        const currentTenantId = this.setTenant(tenantId, tenants, userId);
        const currentTenantName = tenants[currentTenantId]?.name;
        setMonitoringContext({ userId, tenantId: currentTenantId });
        requestMenuData()
          .unwrap()
          .then(({ name, email, showSupport }) => {
            if (showSupport) {
              this.initSupport({
                email,
                name,
                organisation: currentTenantName,
              });
            }
            this.getInitialState().then(() => {
              this.setState({ hasFetchedInitialData: true });
            });
          });
      });
    }
    if (
      (prevProps.locale === '' && locale !== '') ||
      prevProps.locale !== locale
    ) {
      this.getTranslations();
    }
  }

  setTenant = (preferredTenantId, tenants, userId) => {
    if (this.signInWithCredentials) {
      localStorage.setItem(`tenantId_${userId}`, preferredTenantId);
      return preferredTenantId;
    } else {
      const lsTenantId = localStorage.getItem(`tenantId_${userId}`);
      if (lsTenantId === null || !tenants[lsTenantId]) {
        localStorage.setItem(`tenantId_${userId}`, preferredTenantId);
        return preferredTenantId;
      }
      return lsTenantId;
    }
  };

  getInitialState = () => {
    const { logoutOnIdle } = this.state;
    const {
      getPersons,
      getWildcardPersons,
      updateAllowedMimeTypes,
      getGroups,
    } = this.props;
    return Promise.all([
      getLogoutThresholds(),
      getPersons(),
      getWildcardPersons(),
      getGroups(),
      this.getTranslations(),
      updateAllowedMimeTypes(),
    ]).then(([thresholds, ...rest]) => {
      const idleTimeout = Math.min(
        thresholds?.data?.modalThreshold,
        thresholds?.data?.logoutThreshold
      );
      /*         thresholds.data.modalThreshold > thresholds.data.logoutThreshold
          ? thresholds.data.logoutThreshold
          : thresholds.data.modalThreshold; */
      this.setState(
        {
          idleTimeout: idleTimeout,
          logoutThreshold: thresholds?.data?.logoutThreshold,
          logoutTimeout: Math.max(
            0,
            thresholds?.data?.logoutThreshold - thresholds?.data?.modalThreshold
          ),
          modalThreshold: thresholds?.data?.modalThreshold,
          isGettingInitialState: false,
          hasFetchedInitialData: true,
        },
        () => {
          if (logoutOnIdle) {
            this.idleTimer.current?.start();
          }
        }
      );
    });
  };

  getTranslations = () => {
    const { locale } = this.props;
    return getTranslationsService().then(({ data }) => {
      const globalTranslationObject = window.CHAMP.locale.data;
      if (locale === '') return;

      if (!data[locale?.toLowerCase()]?.translations) {
        throw new Error(`Translations for locale "${locale}" not found.`);
      }
      globalTranslationObject.locale = locale;
      globalTranslationObject.map = Object.keys(
        data[locale.toLowerCase()].translations
      ).reduce(
        (acc, key) => ({
          ...acc,
          [key.toLowerCase()]: data[locale.toLowerCase()].translations[key],
        }),
        {}
      );
      this.setState({
        hasTranslations: true,
      });
    });
  };

  getRoutesRecursively(routes) {
    const allRoutes = [];
    routes.forEach((route) => {
      allRoutes.push(route);
      if (route.routes) {
        allRoutes.push(...this.getRoutesRecursively(route.routes));
      }
    });
    return allRoutes;
  }

  initSupport = ({ email, name, organisation }) => {
    initiateSupport();
    resetSupportSession();
    setSupportUserProperties({ email, nickname: name, company: organisation });
  };

  onIdle = () => {
    const { showModal, auth, userFullName, hideModal } = this.props;
    const { signoutRedirect } = auth;
    const { timeouts, modalThreshold, logoutTimeout, logoutThreshold } =
      this.state;
    if (timeouts === 0 && modalThreshold < logoutThreshold) {
      showModal('confirmation', {
        title: localeLookup('translations.Welcome back'),
        closeOnEsc: false,
        closeOnOverlayClick: false,
        dismissable: false,
        infoText: `${localeLookup(
          'translations.You logged in without selecting "Keep me logged in" and you have been inactive for'
        )} ${modalThreshold / 1000 / 60} ${localeLookup(
          'translations.minutes'
        )}`,
        maxWidth: '500px',
        fullWidth: true,
        body: (
          <Text>
            {localeLookup('translations.You are logged in as')}{' '}
            <Text as="span" bold>
              {userFullName}
            </Text>
            {'. '}
            {localeLookup('translations.Do you want to stay logged in?')}
          </Text>
        ),
        confirmButtonText: localeLookup('translations.Stay logged in'),
        btnRejectTitle: localeLookup('translations.Log out'),
        onCancel: () => {
          signoutRedirect({ post_logout_redirect_uri: window.location.origin });
        },
        onConfirm: () => {
          this.setState({ idleTimeout: modalThreshold, timeouts: 0 });
          hideModal();
        },
      });
      this.setState({ idleTimeout: logoutTimeout, timeouts: 1 }, () => {
        this.idleTimer.current.start();
      });
    } else {
      signoutRedirect({ post_logout_redirect_uri: window.location.origin });
    }
  };

  renderRoutes() {
    const routesToRender = this.getRoutesRecursively(Object.values(routes));
    return routesToRender.map((route) => {
      if (!route.component && route.routes) {
        return (
          <Redirect
            exact
            from={route.path}
            key={route.path}
            to={route.routes[0].path}
          />
        );
      }
      return (
        <Route
          exact={route.exact !== undefined ? route.exact : true} // Exact route by default
          key={route.path}
          path={route.path}
          render={(props) => (
            <ErrorBoundary>
              <route.component {...props}></route.component>
            </ErrorBoundary>
          )}
        />
      );
    });
  }

  render() {
    const { menuOpen, toggleMenu, showLoadOverlay, auth } = this.props;
    const { isAuthenticated } = auth;
    const {
      hasTranslations,
      isGettingInitialState,
      hasFetchedInitialData,
      idleTimeout,
      logoutOnIdle,
    } = this.state;
    if (!isAuthenticated || !hasTranslations || !hasFetchedInitialData)
      return null;
    return (
      <div className="app-container">
        {logoutOnIdle && (
          <IdleLogout
            onIdle={this.onIdle}
            ref={this.idleTimer}
            timeout={idleTimeout}
          />
        )}
        <RootNavigator />
        <main className="main">
          <MainHeader />
          <Switch>
            {this.renderRoutes()}
            <Redirect exact from="/login" to="/roles" />
            {this.props.user.isManagingCustomer ? (
              <Redirect exact from="/" to="/editor" />
            ) : (
              <Redirect exact from="/" to="/roles" />
            )}
            {/* <Redirect from="*" exact to="/" /> */}
          </Switch>
          {menuOpen && (
            <div className="navigation__overlay" onClick={toggleMenu} />
          )}
          {showLoadOverlay && <LoadOverlay fixed />}
          <div id="overlay-root"></div>
          <div id="cursor-content-root"></div>
        </main>
        {this.props.blockUI && <div className="system-blockui-overlay" />}
        <ToastContainer />
        <NetworkInfo />
      </div>
    );
  }
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(WithModals(withAuth(App)))
);
